import { useCallback, useState } from 'react';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useImageUploadContext } from 'src/app/shared/hooks/imageUpload';
import { useUploader } from 'src/app/shared/hooks/uppyUploader';

import { useCreatePetitionUpdateContext } from '../../context';
import type { PetitionUpdateFormValues, UseCreatePetitionUpdateFormResult } from '../../types';
import { useCreatePetitionUpdate } from '../useCreatePetitionUpdate';
import { useLocalStoragePetitionUpdateForm, useLocalStoragePetitionUpdateMedia } from '../useLocalStorage';
import { useOpenPreviewModal } from '../useOpenPreviewModal';

import { useGetInitialValues } from './useGetInitialValues';
import { useTrackSubmitEvents } from './useTrackSumbitEvents';

export function useCreatePetitionUpdateForm(): UseCreatePetitionUpdateFormResult {
	const {
		data: {
			petition: { id, slug },
		},
	} = useStarterDashboardV2PageData();
	const isTemplateFilled = useCreatePetitionUpdateContext();
	const { savePetitionUpdateFormValues, removePetitionUpdateFormValues } = useLocalStoragePetitionUpdateForm(
		id,
		isTemplateFilled,
	);
	const openPreviewModal = useOpenPreviewModal();
	const {
		state: { image, imageCrop },
		dispatch,
	} = useImageUploadContext();
	const uploader = useUploader((errorMessage) => dispatch({ type: 'SET_ERROR', payload: { errorMessage } }));
	const [submittedValues, setSubmittedValues] = useState<PetitionUpdateFormValues | EmptyObject>({});
	const { savePetitionUpdateMedia, removePetitionUpdateMedia } = useLocalStoragePetitionUpdateMedia(
		id,
		isTemplateFilled,
	);
	const [createPetitionUpdate, { success, loading, error }] = useCreatePetitionUpdate();
	const { trackOnPreview, trackSubmit } = useTrackSubmitEvents({
		submittedValues,
		removePetitionUpdateFormValues,
		removePetitionUpdateMedia,
		success,
		error,
		image,
	});

	const onPreview = useCallback(
		async (values: PetitionUpdateFormValues) => {
			trackOnPreview();
			await openPreviewModal(values);
		},
		[openPreviewModal, trackOnPreview],
	);

	const onSubmit = useCallback(
		async (values: PetitionUpdateFormValues) => {
			setSubmittedValues(values);
			savePetitionUpdateFormValues(values);
			savePetitionUpdateMedia({
				media: { data: image, crop: imageCrop },
			});
			trackSubmit(!!image);
			await createPetitionUpdate(
				{
					petitionId: id,
					type: 'STARTER',
					...values,
					description: values.description || '',
					title: values.title || '',
				},
				uploader,
			);
		},
		[
			setSubmittedValues,
			savePetitionUpdateFormValues,
			savePetitionUpdateMedia,
			image,
			imageCrop,
			id,
			uploader,
			createPetitionUpdate,
			trackSubmit,
		],
	);

	return {
		data: {
			initialValues: useGetInitialValues(),
			isCurrentlyLoading: loading || !!success,
			uploader,
			success: success?.updateId,
			error: error?.type,
			slug,
		},
		actions: {
			titleMutator: ([value], state, { changeValue }) => {
				changeValue(state, 'title', () => value);
			},
			onSubmit,
			onPreview,
		},
	};
}
